import { ChangeDetectionStrategy, Component } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { combineLatest, map } from 'rxjs';

import { NGXLogger } from 'ngx-logger';
import { AppConfigQuery } from 'src/app/modules/app-config';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { SupportedFeaturesService } from 'src/app/services/supported-features.service';
import { BulkUpdateService } from 'src/app/state/application/bulk-update.service';

import { SearchOperator } from 'models';
import { BulkCSVUpdateService } from 'src/app/services/bulk-csv-update-dialog.service';
import { ArchivesQuery } from 'src/app/state/archives/archives.query';
import { DatabasesQuery } from 'src/app/state/databases/databases.query';
import { SearchesQuery } from 'src/app/state/searches/searches.query';
import { BulkUpdateDialogData } from '../bulk-update-dialog/bulk-update-dialog.component';

@UntilDestroy({ checkProperties: true })
@Component({
  selector: 'app-admin-menu',
  templateUrl: './admin-menu.component.html',
  styleUrls: ['./admin-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AdminMenuComponent {
  /** Is the user an admin of the active database. */
  isDatabaseAdmin$ = this.databasesQuery.activeDbId$.pipe(
    untilDestroyed(this),
    map(
      (dbId) =>
        dbId !== null &&
        dbId !== undefined &&
        (this.auth.user.isAdminAll || this.auth.user.dbAdminOf.includes(dbId))
    )
  );
  /** Should the GA design link be available. */
  isGlobalActionDesignConfigured = !!this.appConfigQuery.globalCaptureUrl;
  /** Should the GA design link be enabled. */
  isGlobalActionDesignEnabled$ = this.databasesQuery.activeDbId$.pipe(
    untilDestroyed(this),
    map((databaseId) => !!databaseId)
  );
  /** Observable of whether bulk CSV update should be shown. */
  showBulkCsvUpdate$ = combineLatest([
    this.isDatabaseAdmin$,
    this.supportedFeatures.versionSupports$(
      this.supportedFeatures.bulkCsvUpdate
    ),
  ]).pipe(
    untilDestroyed(this),
    map(([isAdmin, isSupported]) => isAdmin && isSupported)
  );
  /** Observable of whether bulk update should be shown. */
  showBulkUpdate$ = combineLatest([
    this.isDatabaseAdmin$,
    this.supportedFeatures.versionSupports$(this.supportedFeatures.bulkUpdate),
  ]).pipe(
    untilDestroyed(this),
    map(([isAdmin, isSupported]) => isAdmin && isSupported)
  );
  /** Observable of whether to show search specific bulk update. */
  showBulkUpdateWithCurrentSearch$ = combineLatest([
    this.showBulkUpdate$,
    this.databasesQuery.activeDbId$,
    this.archivesQuery.activeId$,
    this.searchesQuery.activeId$,
  ]).pipe(
    map(
      ([showBulkUpdate, dbId, archiveId, searchId]) =>
        showBulkUpdate && !!dbId && !!archiveId && !!searchId
    )
  );

  constructor(
    private appConfigQuery: AppConfigQuery,
    private databasesQuery: DatabasesQuery,
    private archivesQuery: ArchivesQuery,
    private searchesQuery: SearchesQuery,
    private auth: AuthenticationService,
    private supportedFeatures: SupportedFeaturesService,
    private bulkUpdater: BulkUpdateService,
    private bulkCsvUpdater: BulkCSVUpdateService,
    private logger: NGXLogger
  ) {}
  /**
   * Open full administration web application in a new window.
   */
  openAdmin() {
    window.open(this.appConfigQuery.adminUrl, '_blank');
  }

  /**
   * Open GlobalAction design in a new window.
   */
  openGlobalActionDesign() {
    window.open(
      `${this.appConfigQuery.globalCaptureUrl}#/design?mode=gaw&db=${this.databasesQuery.activeId}&token=${this.auth.user.token}`,
      '_blank'
    );
  }

  /**
   * Open the dialog for the bulk updater.
   *
   *
   * If there is an active search, it will be passed to the bulk update dialog to pre-populate the search parameters.
   */
  openBulkUpdater() {
    let dialogData: BulkUpdateDialogData = {};
    try {
      const searchId = this.searchesQuery.activeId;
      const search = { ...this.searchesQuery.getSearch(searchId) };
      const searchPrompts = this.searchesQuery.currentSearchPrompts;
      search.parameters = search.parameters.map((param) => {
        const prompt = searchPrompts.find((p) => p.id === param.id);
        if (!prompt) return param;
        return {
          ...param,
          value: prompt.value,
        };
      });

      if (search.settings.contentSearch) {
        const contentSearchPrompt = searchPrompts.find((p) => p.id === -1);
        if (contentSearchPrompt) {
          search.parameters.push({
            ...contentSearchPrompt,
            fieldId: 0,
            operator: SearchOperator.keywords,
          });
        }
      }

      dialogData.search = search;
    } catch {
      this.logger.debug(
        'There is no active search to use for bulk update dialog.'
      );
    }
    this.bulkUpdater.promptBulkUpdate(dialogData);
  }

  /*
   * Open the dialog for the bulk updater.
   */
  openBulkCSVUpdater() {
    this.logger.debug('Bulk CSV Update button clicked.');
    this.bulkCsvUpdater
      .openBulkCSVUpdateDialog(this.databasesQuery.activeId)
      .subscribe({
        next: () => {},
        error: (error) =>
          this.logger.error('Error in bulk csv update dialog', error),
      });
  }
  /**
   * Display license manager web application in a new window.
   */
  openLicenseManager() {
    window.open(this.appConfigQuery.licenseManagerUrl, '_blank');
  }

  /**
   * Open user management web application in a new window.
   */
  openUserManagement() {
    window.open(this.appConfigQuery.userManagementUrl, '_blank');
  }
}
